import useBlueprintContext from "../context/useBlueprintContext";
import {
  toggleBlueprintIsLongRunning,
  toggleExcludeModelSyncedWebhook,
  updateBlueprintName,
} from "../utils/BlueprintEditorAPIClient";
import { useHistory } from "react-router-dom";
import { getStaleParameterValueKeysForStep } from "../utils/BlueprintEditorUtils";
import { EditableText } from "../../shared/EditableText";
import MergeText, { TextType } from "../../shared/text/MergeText";
import { fetchBlueprintReadme, updateBlueprintReadme } from "../utils/BlueprintEditorAPIClient";
import OverviewReadmePanel from "../../shared/readme/OverviewReadmePanel";
import OverviewSection from "../../shared/layout/OverviewSection";
import { useState } from "react";
import { BlueprintStep, LEFT_ALIGN_COOKIE_KEY } from "../../../models/Blueprints";
import { showErrorToast, showSuccessToast } from "../../shared/Toasts";
import MergeCopyToClipboard from "../../shared/MergeCopyToClipboard";
import {
  Badge,
  Button,
  ButtonVariant,
  Checkbox,
  CopyTextField,
  Text,
  Tooltip,
} from "@merge-api/merge-javascript-shared";
import { navigateToIndividualReportTemplate } from "../../../router/RouterUtils";
import CheckboxFormField from "../right-panel/CheckboxFormField";
import { isLeftAlignEnabled } from "../utils/BlueprintCanvasUtils";
import { useCookies } from "react-cookie";
import { ExternalLink, Info } from "lucide-react";

const humanizeEnum = (string: String) => {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

const BlueprintEditorLeftPanelOverviewSubtab = () => {
  const {
    blueprint,
    setBlueprintHumanName,
    enableViewportCulling,
    setEnableViewportCulling,
  } = useBlueprintContext();
  const {
    name,
    operation_type,
    mapped_models,
    version,
    endpoints,
    id,
    human_name,
    integration,
    is_long_running,
    report_template_id,
    exclude_from_model_synced_webhook,
  } = blueprint;

  const [isLongRunning, setIsLongRunning] = useState<boolean>(is_long_running ?? false);
  const [excludeFromSyncedWebhook, setExcludeFromSyncedWebhook] = useState<boolean>(
    exclude_from_model_synced_webhook ?? false
  );
  const history = useHistory();

  const [cookies, setCookie] = useCookies([LEFT_ALIGN_COOKIE_KEY]);

  const findStaleParamsRecursively = (
    step: BlueprintStep,
    result: { [step_id: string]: Set<string> }
  ) => {
    const staleKeys = getStaleParameterValueKeysForStep(blueprint, step);
    // If we find stale keys, add it to the result
    if (staleKeys.size > 0) result[step.id] = staleKeys;

    // Recursive case
    Object.entries(step?.paths ?? []).forEach(([_, path]) => {
      path.forEach((step) => findStaleParamsRecursively(step, result));
    });

    return result;
  };

  const onSuccess = (response: any) => {
    setBlueprintHumanName(response.human_name);
  };

  const handleToggleBlueprintIsLongRunning = (newIsLongRunningValue: boolean) => {
    toggleBlueprintIsLongRunning({
      blueprintID: blueprint.id,
      isLongRunning: newIsLongRunningValue,
      onSuccess: (_: any) => {
        showSuccessToast("Successfully updated is_long_running value for Blueprint");
        setIsLongRunning(newIsLongRunningValue);
      },
      onError: (_: any) => {
        showErrorToast("Failed to update is_long_running value for Blueprint");
      },
    });
  };

  const handleToggleExcludeModelSyncedWebhook = (newExcludeModelSyncedWebhook: boolean) => {
    toggleExcludeModelSyncedWebhook({
      blueprintID: blueprint.id,
      excludeModelSyncedWebhook: newExcludeModelSyncedWebhook,
      onSuccess: (_: any) => {
        showSuccessToast(
          "Successfully updated exclude_from_model_synced_webhook value for Blueprint"
        );
        setExcludeFromSyncedWebhook(newExcludeModelSyncedWebhook);
      },
      onError: (_: any) => {
        showErrorToast("Failed to update exclude_from_model_synced_webhook value for Blueprint");
      },
    });
  };

  const commonModelsPills = (
    <div>
      {mapped_models.length > 0 ? (
        mapped_models.map((model) => <Badge className="gray">{model}</Badge>)
      ) : (
        <MergeText size="14px" type={TextType.MUTED2} isBold>
          None
        </MergeText>
      )}
    </div>
  );

  const docsLink = integration.api_documentation_url ? (
    <Button
      variant={ButtonVariant.TextBlue}
      size="sm"
      rightIcon={<ExternalLink size={12} />}
      onClick={() => {
        window.open(integration.api_documentation_url, "_blank");
      }}
    >
      API documentation
    </Button>
  ) : (
    <Text variant="h6">None</Text>
  );

  const APIEndpointsPills = (
    <div>
      {endpoints.length > 0 ? (
        endpoints.map((endpoint) => (
          <Badge className="blue">{`${endpoint.method} ${endpoint.path}`}</Badge>
        ))
      ) : (
        <Text variant="h6">None</Text>
      )}
    </div>
  );

  const onSaveBlueprintName = (newName: string) => {
    updateBlueprintName({ name: newName, blueprintID: id, onSuccess });
  };

  // Certain fields will be rendered conditionally, depending ont he operation type of the Blueprint
  const sections = [
    <OverviewSection
      title="Blueprint name"
      overviewBodyTextClassName="w-100"
      content={<EditableText originalText={human_name || name} onSave={onSaveBlueprintName} />}
    />,
    <OverviewSection title="Blueprint ID" content={<CopyTextField text={blueprint.id} />} />,
    <OverviewSection
      title="Blueprint version ID"
      content={<CopyTextField text={blueprint.version.id} />}
    />,
    <OverviewSection title="Publish status" content={humanizeEnum(version.publish_state ?? "")} />,
    <OverviewSection title="Blueprint type" content={<>{humanizeEnum(operation_type ?? "")}</>} />,
    <OverviewSection title="API endpoints used" content={APIEndpointsPills} />,
    <OverviewSection title="Created or updated Common Models" content={commonModelsPills} />,
    report_template_id ? (
      <OverviewSection
        title="Report template ID"
        linkName="Report Template"
        onClick={() =>
          navigateToIndividualReportTemplate(history, integration.id, report_template_id, true)
        }
        content={
          <div className="d-flex align-items-center">
            <Text className="mr-1.5" variant="h6">
              {report_template_id}
            </Text>
            <MergeCopyToClipboard textToCopy={report_template_id} />
          </div>
        }
      />
    ) : (
      <></>
    ),
    <OverviewSection title={`${integration.name} API documentation`} content={docsLink} />,
    <OverviewSection
      title="Long-running blueprints"
      content={
        <>
          <div className="flex items-center mb-1">
            <Checkbox
              checked={isLongRunning}
              onChange={(value) => handleToggleBlueprintIsLongRunning(value)}
              label={
                <Text as="div" variant="h6">
                  Mark as long-running
                </Text>
              }
            />
            <Tooltip
              title="Blueprints that are long running get sent to our slow-blueprint celery queue. The
      pods in this queue have more memory than fast-blueprint pods, and support concurrency"
              placement="top"
            >
              <Info size={16} className="ml-1.5 text-gray-40" />
            </Tooltip>
          </div>
          <div className="flex items-center">
            <Checkbox
              checked={excludeFromSyncedWebhook}
              disabled={!isLongRunning}
              onChange={(value) => handleToggleExcludeModelSyncedWebhook(value)}
              label={
                <Text as="div" variant="h6">
                  Exclude from synced webhooks
                </Text>
              }
            />
            <Tooltip
              title="If enabled, executions for this BP will be excluded when calculating whether or not to send a model synced webhook, unless they have completed. Learn more in Notion"
              placement="top"
            >
              <Info size={16} className="ml-1.5 text-gray-40" />
            </Tooltip>
          </div>
        </>
      }
    />,
    <OverviewSection
      title="Concurrency version"
      content={
        <Text variant="h6">
          {version.can_run_with_unbound_concurrency === true
            ? "v2"
            : version.can_run_with_unbound_concurrency === false
            ? "v1"
            : "not retrieved"}
        </Text>
      }
    />,
    <OverviewReadmePanel
      fetchReadme={(props) => fetchBlueprintReadme({ blueprintID: blueprint.id, ...props })}
      updateReadme={(props) => updateBlueprintReadme({ blueprintID: blueprint.id, ...props })}
    />,
    <OverviewSection
      title="BPE settings"
      content={
        <div className="flex flex-col">
          <CheckboxFormField
            title="Left align steps"
            currentValue={isLeftAlignEnabled(cookies.leftAlignSteps)}
            onChange={(newValue) => {
              // Both states are stored, since an absent cookie now means left-aligned.
              setCookie("leftAlignSteps", newValue, { path: "/" });
            }}
          />
          <CheckboxFormField
            title="Enable smart rendering"
            currentValue={enableViewportCulling}
            onChange={(newValue) => {
              setEnableViewportCulling(newValue);
              const params = new URLSearchParams(history.location.search);
              if (newValue) {
                params.delete("enable_smart_rendering");
              } else {
                params.set("enable_smart_rendering", "false");
              }
              history.replace({ search: params.toString() });
            }}
          />
        </div>
      }
    />,
  ];

  return <div className="flex flex-col gap-y-6">{sections.map((section) => section)}</div>;
};

export default BlueprintEditorLeftPanelOverviewSubtab;
